import { Phone, CheckCircle, PawPrint, BadgeCheck, UserCheck, MessageCircle, Wind, Award, Calendar, Users, ThumbsUp, Building2 } from "lucide-react";
import WhatsAppIcon from "@/components/ads/WhatsAppIcon";
import { useTrackedCTA } from "@/hooks/useTrackedCTA";
import inseticidaConvencional from "@/assets/ads/inseticida-convencional.webp";
import inseticidaUSA from "@/assets/ads/inseticida-usa.webp";
import AdsLandingLayout from "@/components/ads/AdsLandingLayout";
import ClientLogos from "@/components/ClientLogos";

const badges = [
  "Seguro após a secagem",
  "Produtos aprovados pela ANVISA",
  "Garantia de resultado",
];

const seguranca = [
  { icon: BadgeCheck, title: "Produto profissional aprovado pela ANVISA" },
  { icon: UserCheck, title: "Aplicação por equipe treinada" },
  { icon: MessageCircle, title: "Orientação antes e depois do serviço" },
  { icon: PawPrint, title: "Segurança para pets e crianças após a secagem" },
  { icon: Wind, title: "Método sem cheiro forte na maioria dos casos" },
  { icon: Award, title: "Garantia e acompanhamento" },
];

const provasIcons = [Calendar, Users, ThumbsUp, Building2];

const comum = [
  "Pode ter cheiro forte",
  "Pode desalojar insetos",
  "Pouca orientação pós-serviço",
  "Foco apenas na aplicação",
];

const usa = [
  "Produto exclusivo",
  "Aplicação estratégica",
  "Não desaloja insetos de forma agressiva",
  "Acompanhamento e garantia",
  "Pensado para residências com família e pets",
];

const provas = [
  "Mais de 20 anos de mercado",
  "Mais de 50.000 clientes atendidos",
  "Resultado satisfatório ou seu dinheiro de volta",
  "Atendimento residencial, comercial e condomínios",
];

const faqs = [
  { q: "É seguro para cachorro e gato?", a: "Sim, seguindo as orientações do técnico e aguardando a secagem do produto." },
  { q: "Crianças podem ficar em casa?", a: "Na maioria dos casos sim, mas a orientação final depende do ambiente e da aplicação." },
  { q: "Fica cheiro forte?", a: "Na maioria dos serviços, o método não deixa cheiro forte. A equipe informa o procedimento correto antes da aplicação." },
  { q: "O produto é aprovado?", a: "Sim, são utilizados produtos profissionais aprovados pela ANVISA." },
  { q: "Tem garantia?", a: "Sim, a empresa oferece resultado satisfatório ou seu dinheiro de volta." },
];

const DedetizacaoSegura = () => {
  const { handleWhatsAppClick, handlePhoneClick, whatsAppUrl, phone } = useTrackedCTA();

  return (
    <AdsLandingLayout
        title="Dedetização Segura para Pets e Crianças | Dedetizadora USA"
        description="Dedetização segura para residências, pets e crianças após a secagem. Produto profissional, aplicação técnica e garantia de resultado. Orçamento grátis."
      >
        {/* Hero */}
        <section id="hero" className="relative text-white py-12 md:py-20 px-4 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="/images/hero-800.webp"
              srcSet="/images/hero-800.webp 800w, /images/hero-1280.webp 1280w, /images/hero.webp 1920w"
              sizes="100vw"
              alt="Profissional da Dedetizadora USA realizando controle de pragas"
              className="w-full h-full object-cover object-top"
              width={1920}
              height={1080}
              fetchPriority="high"
              decoding="async"
            />
            <div
              className="absolute inset-0"
              style={{ background: "linear-gradient(135deg, rgba(29,138,79,0.55), rgba(13,74,42,0.85))" }}
              aria-hidden="true"
            />
            <div
              className="absolute inset-0 opacity-60"
              style={{ background: "radial-gradient(circle at 30% 20%, #22c46a33, transparent 60%)" }}
              aria-hidden="true"
            />
          </div>
          <div className="relative max-w-4xl mx-auto text-center">
            <h1 className="text-3xl md:text-5xl font-bold mb-4 leading-tight">
              Dedetização segura para sua família, pets e crianças
            </h1>
            <p className="text-base md:text-xl mb-6 opacity-95 max-w-3xl mx-auto">
              Controle profissional de pragas com aplicação técnica, produto aprovado pela ANVISA e
              garantia de resultado satisfatório ou seu dinheiro de volta.
            </p>
            <ul className="flex flex-wrap justify-center gap-2 mb-8">
              {badges.map((b) => (
                <li
                  key={b}
                  className="bg-white/10 border border-white/30 rounded-full px-3 py-1 text-xs md:text-sm"
                >
                  {b}
                </li>
              ))}
            </ul>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href={whatsAppUrl}
                onClick={handleWhatsAppClick("ads_segura_hero_whatsapp")}
                aria-label="Solicitar orçamento seguro no WhatsApp"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-bold animate-cta-attention"
                style={{ backgroundColor: "#22c46a", color: "#0a1f14", boxShadow: "0 12px 40px -10px #22c46a99" }}
              >
                <WhatsAppIcon size={22} />
                Solicitar Orçamento Seguro no WhatsApp
              </a>
              <a
                href={phone}
                onClick={handlePhoneClick("ads_segura_hero_phone")}
                aria-label="Ligar agora"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-bold border-2 border-white/80 text-white hover:bg-white/10 animate-cta-attention"
              >
                <Phone className="w-5 h-5" />
                Ligar Agora
              </a>
            </div>
          </div>
        </section>

        {/* Empatia */}
        <section id="empatia" className="py-12 md:py-16 px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
              Você quer eliminar as pragas, mas não quer colocar sua família em risco.
            </h2>
            <p className="text-muted-foreground">
              Esse cuidado é correto. Dedetização residencial precisa ser feita com produto adequado,
              dose técnica, aplicação nos pontos certos e orientação clara para o pós-serviço.
            </p>
          </div>
        </section>

        {/* Segurança */}
        <section id="seguranca" className="bg-muted py-12 md:py-16 px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-8 text-center">
              O que torna a aplicação mais segura?
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {seguranca.map(({ icon: Icon, title }) => (
                <div key={title} className="bg-card border border-border rounded-lg p-5 flex items-start gap-3">
                  <Icon className="w-6 h-6 text-primary shrink-0 mt-0.5" />
                  <span className="text-card-foreground text-sm font-medium">{title}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Comparação */}
        <section id="comparacao" className="py-12 md:py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-8 text-center">
              Dedetização comum x Método <span className="text-highlight">USA</span>
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-card border border-border rounded-lg overflow-hidden">
                <img
                  src={inseticidaConvencional}
                  alt="Inseticidas convencionais não tecnológicos, mais agressivos e tóxicos"
                  width={1200}
                  height={800}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-40 sm:h-48 md:h-52 object-cover"
                />
                <div className="p-5">
                  <h3 className="font-bold text-card-foreground mb-3">Dedetização comum</h3>
                  <ul className="space-y-2">
                    {comum.map((c) => (
                      <li key={c} className="text-sm text-muted-foreground flex gap-2">
                        <span className="text-destructive">×</span> {c}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="bg-primary/5 border-2 border-primary rounded-lg overflow-hidden">
                <img
                  src={inseticidaUSA}
                  alt="Inseticida USA, fórmula moderna, segura para família e pets"
                  width={1200}
                  height={800}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-40 sm:h-48 md:h-52 object-cover"
                />
                <div className="p-5">
                  <h3 className="font-bold text-primary mb-3">Método <span className="text-highlight">USA</span></h3>
                  <ul className="space-y-2">
                    {usa.map((u) => (
                      <li key={u} className="text-sm text-foreground flex gap-2">
                        <CheckCircle className="w-4 h-4 text-primary shrink-0 mt-0.5" /> {u}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Mecanismo único */}
        <section id="metodo" className="bg-muted py-12 md:py-16 px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
              Produto que age em cadeia sem espalhar o problema
            </h2>
            <p className="text-muted-foreground">
              O método <span className="text-highlight font-semibold">USA</span> busca atingir a colônia de insetos sem provocar fuga desordenada. Isso
              ajuda a manter o controle do ambiente e reduz o risco de atrair predadores como
              escorpiões.
            </p>
          </div>
        </section>

        {/* Prova / Confiança */}
        <section id="prova" className="py-12 md:py-16 px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-8">
              Confiança construída em mais de 20 anos
            </h2>
            <ul className="grid sm:grid-cols-2 gap-3 text-left">
              {provas.map((p, i) => {
                const Icon = provasIcons[i] ?? BadgeCheck;
                return (
                  <li key={p} className="flex items-start gap-2 bg-card border border-border rounded-lg p-3">
                    <Icon className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                    <span className="text-card-foreground text-sm">{p}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="bg-muted py-12 md:py-16 px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-8 text-center">
              Perguntas frequentes
            </h2>
            <div className="space-y-3">
              {faqs.map((f) => (
                <details key={f.q} className="bg-card border border-border rounded-lg p-4 group">
                  <summary className="font-semibold text-card-foreground cursor-pointer list-none flex justify-between items-center">
                    {f.q}
                    <span className="text-primary group-open:rotate-45 transition-transform">+</span>
                  </summary>
                  <p className="mt-3 text-sm text-muted-foreground">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Clientes */}
        <ClientLogos />

        {/* CTA final */}
        <section id="cta-final" className="relative text-white py-16 md:py-20 px-4 text-center overflow-hidden">
          <div
            className="absolute inset-0"
            style={{ background: "linear-gradient(135deg, #1d8a4f, #0d4a2a)" }}
            aria-hidden="true"
          />
          <div
            className="absolute inset-0 opacity-70"
            style={{ background: "radial-gradient(circle at 70% 30%, #22c46a33, transparent 60%)" }}
            aria-hidden="true"
          />
          <div className="relative max-w-3xl mx-auto">
            <CheckCircle className="w-10 h-10 mx-auto mb-4" style={{ color: "#22c46a" }} />
            <h2 className="text-2xl md:text-4xl font-bold mb-3">
              Proteja sua casa sem colocar sua família em risco
            </h2>
            <p className="mb-6 opacity-95 max-w-2xl mx-auto">
              Fale com a equipe e receba uma orientação segura pelo WhatsApp.
            </p>
            <a
              href={whatsAppUrl}
              onClick={handleWhatsAppClick("ads_segura_final_whatsapp")}
              aria-label="Solicitar orçamento no WhatsApp"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-bold animate-cta-attention"
              style={{ backgroundColor: "#22c46a", color: "#0a1f14", boxShadow: "0 12px 40px -10px #22c46a99" }}
            >
              <WhatsAppIcon size={22} />
              Solicitar Orçamento no WhatsApp
            </a>
          </div>
        </section>
    </AdsLandingLayout>
  );
};

export default DedetizacaoSegura;
